import React, { useState } from "react";
import Loader from "../../../components/Loader";
import CommonDataGrid from "../../../components/CommonDataGrid";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal";
import { Toaster } from "react-hot-toast";
import { GridColDef } from "@mui/x-data-grid";
import useTransactions from "../useTransaction";
// Import SelectChangeEvent for explicit typing
import {
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Grid,
  Paper,
  Box,
  SelectChangeEvent,
} from "@mui/material";
import { getSortByTimeButtonText } from "../../../util/getSortByTimeText";
import Button from "../../../components/Botton";

const Transactions: React.FC = () => {
  const columns: GridColDef[] = [
    { field: "transactionId", headerName: "Transaction ID", align: "left", headerAlign: "left", width: 160, filterable: true },
    { field: "userName", headerName: "User Name", align: "center", headerAlign: "center", flex: 1, filterable: true },
    { field: "productName", headerName: "Product Name", align: "center", headerAlign: "center", flex: 1, filterable: true },
    { field: "productType", headerName: "Product Type", align: "center", headerAlign: "center", flex: 0.6, filterable: true },
    {
      field: "amount",
      headerName: "Amount",
      align: "center",
      headerAlign: "center",
      flex: 0.6,
      filterable: true,
      renderCell: (params) => `₹${params.value}`,
    },
    {
      field: "status",
      headerName: "Status",
      align: "center",
      headerAlign: "center",
      width: 80,
      filterable: true,
      renderCell: (params) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-semibold ${
            params.value ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"
          }`}
        >
          {params.value ? "Success" : "Pending"}
        </span>
      ),
    },
    { field: "mobileNo", headerName: "Mobile", align: "center", headerAlign: "center", filterable: true },
    {
      field: "pdfUrl",
      headerName: "Invoice",
      align: "center",
      headerAlign: "center",
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: (params) =>
        params.row.pdfUrl ? (
          <a
            href={params.row.pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-500 underline"
          >
            View PDF
          </a>
        ) : (
          "No Invoice"
        ),
    },
    {
      field: "createdAt",
      headerName: "Timestamp",
      align: "center",
      headerAlign: "center",
      width: 160,
      filterable: true,
      renderCell: (params) => new Date(params.row.createdAt).toLocaleString(),
    },
  ];

  const { transactionsData, loading, filters, setFilters, setFilterModel, setSortingModel, rowCount, paginationModel, setPaginationModel, handleSortByTime, timeSortOrder } =
    useTransactions(columns);

  // Modal state remains for potential future use
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState<string | null>(null);

  /**
   * Handles value changes from both TextField and Select components.
   * It uses a TypeScript union type to accept events from either component,
   * making the handler more robust and type-safe.
   */
  const handleFilterChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent
  ) => {
    const { name, value } = e.target;

    // The 'name' property from the event target is used as the key
    // to update the corresponding filter in the state.
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };


  return (
    <div className="mx-auto p-4">
      {loading && <Loader />}

      <Paper elevation={2} className="p-4 mb-4">
        <Box component="form" noValidate autoComplete="off">
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Search"
                variant="outlined"
                name="searchQuery"
                value={filters.searchQuery}
                onChange={handleFilterChange}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Product Type</InputLabel>
                <Select
                  name="productType"
                  value={filters.productType}
                  label="Product Type"
                  onChange={handleFilterChange}
                >
                  <MenuItem value="">
                    <em>All</em>
                  </MenuItem>
                  <MenuItem value="E-Book">E-Book</MenuItem>
                  <MenuItem value="Test">Test</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select name="status" value={filters.status} label="Status" onChange={handleFilterChange}>
                  <MenuItem value="">
                    <em>All</em>
                  </MenuItem>
                  <MenuItem value="true">Success</MenuItem>
                  <MenuItem value="false">Pending</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                label="Min Amount"
                variant="outlined"
                name="amountMin"
                type="number"
                value={filters.amountMin}
                onChange={handleFilterChange}
                size="small"
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                label="Max Amount"
                variant="outlined"
                name="amountMax"
                type="number"
                value={filters.amountMax}
                onChange={handleFilterChange}
                size="small"
              />
            </Grid>
          </Grid>
        </Box>
      </Paper>

      <div className="mt-4 bg-white p-4 rounded-lg shadow-md">
        <div className="flex justify-end items-center gap-4 mb-4">
          <Button
            onClick={handleSortByTime}
            className="py-1.5 px-9 rounded"
            children={getSortByTimeButtonText(timeSortOrder)}
            type="button"
          />
        </div>
        <CommonDataGrid
          id="transactions"
          rows={transactionsData}
          columns={columns}
          loading={loading}
          setFilterModel={setFilterModel}
          setSortingModel={setSortingModel}
          // Server Pagination Props
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          // pageSizeOptions={[10, 25, 50]}
          // Other props
          showConfirmation={false} // or manage with state
          confirmationMessage=""
          onCancelDelete={() => {}}
          onConfirmDelete={() => {}}
          toolbarProps={{ toolbar: { showQuickFilter: true } }}
        />
      </div>

      <Toaster />
    </div>
  );
};

export default Transactions;
